/*
 * Reels — Lotus
 *
 *  - Click a reel: it scales up, plays unmuted; siblings shrink + dim.
 *  - Click another reel: switches.
 *  - Click outside any card / press Escape: deactivates (pauses + mutes + restores layout).
 *  - IntersectionObserver pauses + mutes the active reel when the section leaves the viewport
 *    (covers scroll, tab switch, etc.). When the section comes back into view we DO NOT
 *    auto-resume — user must click again.
 */
(function () {
  'use strict';

  var SECTION_VISIBLE_THRESHOLD = 0.35;

  function initSection(section) {
    if (section.dataset.reelsInit === '1') return;
    section.dataset.reelsInit = '1';

    var wrap = section.querySelector('[data-reels-wrap]');
    var rail = section.querySelector('[data-reels-rail]');
    var prevBtn = section.querySelector('[data-reels-prev]');
    var nextBtn = section.querySelector('[data-reels-next]');
    var marqueeOn = wrap && wrap.getAttribute('data-reels-marquee') === 'true';
    var track = rail ? rail.querySelector('.reels-lotus__track') : null;

    // In marquee mode, clone the real cards into the track so the seamless loop is
    // fully functional (every clone gets its own toggle button, <video>, handlers,
    // etc.) rather than static thumbnails. Done BEFORE handler binding so the
    // forEach below picks up the clones too.
    if (marqueeOn && track) {
      var originals = Array.prototype.slice.call(track.querySelectorAll('[data-reel-card]'));
      originals.forEach(function (orig) {
        var clone = orig.cloneNode(true);
        clone.setAttribute('data-reel-clone', '1');
        clone.classList.remove('reel-card--initial-center');
        // Strip ids on cloned children to avoid duplicate-id warnings (videos use
        // tag-based queries, not ids, so this is safe).
        clone.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
        track.appendChild(clone);
      });
    }

    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-reel-card]'));
    if (!cards.length) return;

    var activeCard = null;
    var sectionVisible = false;

    function hydrateVideo(v) {
      if (!v || v.dataset.hydrated === '1') return;
      var src = v.getAttribute('data-src');
      if (!src) return;
      var source = document.createElement('source');
      source.src = src;
      var type = v.getAttribute('data-type');
      if (type) source.type = type;
      v.appendChild(source);
      // Switch from preload=none to metadata once it's actually wanted.
      v.preload = 'metadata';
      v.dataset.hydrated = '1';
      try { v.load(); } catch (_) {}
    }

    function centerCardInRail(card, smooth) {
      if (!rail || !card) return;
      var cardRect = card.getBoundingClientRect();
      var railRect = rail.getBoundingClientRect();
      var offsetWithinRail = (cardRect.left + cardRect.width / 2) - railRect.left;
      var target = rail.scrollLeft + offsetWithinRail - rail.clientWidth / 2;
      // Clamp so we don't trigger overscroll-snap pushback that some browsers route to the window.
      target = Math.max(0, Math.min(target, rail.scrollWidth - rail.clientWidth));
      if (smooth) rail.scrollTo({ left: target, behavior: 'smooth' });
      else rail.scrollLeft = target;
    }

    function pauseVideo(card) {
      var v = card.querySelector('[data-reel-video]');
      if (!v) return;
      try { v.pause(); } catch (_) {}
      v.muted = true;
      v.currentTime = 0;
    }

    function deactivate() {
      if (!activeCard) return;
      pauseVideo(activeCard);
      activeCard.classList.remove('reel-card--active');
      activeCard = null;
      section.classList.remove('reels-lotus--has-active');
    }

    function activate(card) {
      if (activeCard === card) {
        deactivate();
        return;
      }
      if (activeCard) {
        pauseVideo(activeCard);
        activeCard.classList.remove('reel-card--active');
      }
      activeCard = card;
      activeCard.classList.add('reel-card--active');
      section.classList.add('reels-lotus--has-active');

      // Center the active card horizontally — rail-local scroll only, never page scroll.
      centerCardInRail(card, true);

      var v = card.querySelector('[data-reel-video]');
      if (v) {
        hydrateVideo(v);
        // Only attempt sound if the section is currently visible.
        v.muted = !sectionVisible;
        var playPromise = v.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(function () {
            // Autoplay-with-sound blocked — fall back to muted playback.
            v.muted = true;
            v.play().catch(function () {});
          });
        }
      }
    }

    cards.forEach(function (card) {
      var toggle = card.querySelector('[data-reel-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        activate(card);
      });
    });

    // Click anywhere outside an active card → deactivate.
    document.addEventListener('click', function (e) {
      if (!activeCard) return;
      if (e.target.closest('[data-reel-card]')) return; // clicks on cards handled above
      // Ignore clicks on the bag link / shop links inside section
      if (e.target.closest('[data-reels-section]') === section && e.target.closest('a')) return;
      deactivate();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && activeCard) deactivate();
    });

    // Center the visually-flagged middle card on first paint (rail-local scroll, NOT scrollIntoView,
    // so the page's vertical scroll position is never touched).
    var initialCenter = section.querySelector('.reel-card--initial-center');
    if (initialCenter && rail) {
      requestAnimationFrame(function () {
        centerCardInRail(initialCenter, false);
      });
    }

    // Hydrate all videos in the section once it gets close to the viewport. Cheap pre-warm
    // so the first click feels instant, but we don't ship the bytes during initial page load.
    if ('IntersectionObserver' in window) {
      var preloadObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          cards.forEach(function (c) { hydrateVideo(c.querySelector('[data-reel-video]')); });
          preloadObserver.disconnect();
        });
      }, { rootMargin: '200px 0px' });
      preloadObserver.observe(section);
    }

    // Pause + mute the active reel when section scrolls out of view; un-mute again on scroll-back.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          sectionVisible = entry.isIntersecting && entry.intersectionRatio >= SECTION_VISIBLE_THRESHOLD;
          if (!sectionVisible && activeCard) {
            // Pause but don't deactivate the visual scale state — user may scroll back.
            var v = activeCard.querySelector('[data-reel-video]');
            if (v) {
              try { v.pause(); } catch (_) {}
              v.muted = true;
            }
          } else if (sectionVisible && activeCard) {
            // Coming back into view — keep audio off until user explicitly clicks again.
            var v2 = activeCard.querySelector('[data-reel-video]');
            if (v2) v2.muted = true;
          }
        });
      }, { threshold: [0, SECTION_VISIBLE_THRESHOLD, 1] }).observe(section);
    }

    // Pause active video when tab/window loses focus.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && activeCard) {
        var v = activeCard.querySelector('[data-reel-video]');
        if (v) try { v.pause(); } catch (_) {}
      }
    });

    /* ===== Drag-to-scroll (mouse only — touch already scrolls natively) ===== */
    if (rail) {
      var isDown = false;
      var dragStartX = 0;
      var dragStartScroll = 0;
      var dragMoved = false;
      var DRAG_THRESHOLD = 5;

      rail.addEventListener('mousedown', function (e) {
        if (e.button !== 0) return;
        isDown = true;
        dragMoved = false;
        dragStartX = e.pageX;
        dragStartScroll = rail.scrollLeft;
      });
      window.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        var dx = e.pageX - dragStartX;
        if (!dragMoved && Math.abs(dx) > DRAG_THRESHOLD) {
          dragMoved = true;
          rail.classList.add('is-grabbing');
          pauseAutoScroll();
        }
        if (dragMoved) {
          e.preventDefault();
          rail.scrollLeft = dragStartScroll - dx;
        }
      });
      window.addEventListener('mouseup', function () {
        if (!isDown) return;
        isDown = false;
        if (dragMoved) {
          rail.classList.remove('is-grabbing');
          // Suppress the next click so card-toggle doesn't fire when releasing a drag.
          var suppress = function (ev) { ev.stopPropagation(); ev.preventDefault(); };
          rail.addEventListener('click', suppress, { capture: true, once: true });
          scheduleAutoScrollResume();
        }
      });
    }

    /* ===== Step buttons ===== */
    function step(dir) {
      if (!rail) return;
      var firstCard = cards[0];
      var stepWidth = (firstCard ? firstCard.getBoundingClientRect().width : 240) + 24; // card + gap
      pauseAutoScroll();
      rail.scrollBy({ left: dir * stepWidth, behavior: 'smooth' });
      scheduleAutoScrollResume();
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    function updateNavState() {
      if (!rail || !prevBtn || !nextBtn) return;
      var atStart = rail.scrollLeft <= 1;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
      // In marquee mode buttons are always live (the loop wraps), so don't disable.
      if (!marqueeOn) {
        prevBtn.disabled = atStart;
        nextBtn.disabled = atEnd;
      }
    }
    if (rail) rail.addEventListener('scroll', updateNavState, { passive: true });
    setTimeout(updateNavState, 50);
    window.addEventListener('resize', updateNavState);

    /* ===== JS-driven auto-scroll (replaces the old CSS @keyframes marquee) =====
       Drives rail.scrollLeft via rAF so drag + buttons + auto-scroll all share the
       same scroll position. When we cross half the scrollWidth (server emits a
       duplicated set of cards in marquee mode), we jump back by half — seamless loop. */
    var autoScrollRAF = null;
    var autoScrollPaused = false;
    var pauseTimer = null;

    function startAutoScroll() {
      if (!marqueeOn || !rail || autoScrollRAF) return;
      // Speed: full loop in `marquee_speed` seconds. Loop length = scrollWidth / 2 (duplicated set).
      var loopLen = rail.scrollWidth / 2 || 800;
      var durationSec = parseFloat(getComputedStyle(section).getPropertyValue('--reels-marquee-duration')) || 60;
      var pxPerMs = loopLen / (durationSec * 1000);
      var last = performance.now();
      var tick = function (now) {
        if (autoScrollPaused) { autoScrollRAF = null; return; }
        var dt = now - last;
        last = now;
        rail.scrollLeft += pxPerMs * dt;
        if (rail.scrollLeft >= loopLen) rail.scrollLeft -= loopLen; // seamless wrap
        autoScrollRAF = requestAnimationFrame(tick);
      };
      autoScrollRAF = requestAnimationFrame(tick);
    }

    function pauseAutoScroll() {
      autoScrollPaused = true;
      if (autoScrollRAF) {
        cancelAnimationFrame(autoScrollRAF);
        autoScrollRAF = null;
      }
      if (pauseTimer) { clearTimeout(pauseTimer); pauseTimer = null; }
    }

    function scheduleAutoScrollResume() {
      if (!marqueeOn) return;
      if (pauseTimer) clearTimeout(pauseTimer);
      pauseTimer = setTimeout(function () {
        autoScrollPaused = false;
        startAutoScroll();
      }, 2500);
    }

    // Pause on hover over the rail / when a card is active.
    if (rail && marqueeOn) {
      rail.addEventListener('mouseenter', pauseAutoScroll);
      rail.addEventListener('mouseleave', scheduleAutoScrollResume);
    }

    // Only run auto-scroll while section is in view; pause when off-screen.
    if (marqueeOn && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
            // Don't resume while a card is active.
            if (!activeCard) {
              autoScrollPaused = false;
              startAutoScroll();
            }
          } else {
            pauseAutoScroll();
          }
        });
      }, { threshold: [0, 0.1, 0.5] }).observe(section);
    }

    // When a card becomes active, stop the loop. When deactivated, resume.
    var origActivate = activate;
    activate = function (card) {
      pauseAutoScroll();
      origActivate(card);
    };
    var origDeactivate = deactivate;
    deactivate = function () {
      origDeactivate();
      scheduleAutoScrollResume();
    };
  }

  function init() {
    document.querySelectorAll('[data-reels-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
